
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import Session from "./Session"
import { useSearchParams } from "react-router-dom"
import { useAppFwContext } from "../AppFwContextContextProvider.tsx";

export interface SessionControl {
    addLoginListener(listener: (session: Session) => Promise<void> | void): () => void
    addLogoutListener(listener: () => Promise<void> | void): () => void
}

export const SessionContext = createContext<undefined | null | Session>(undefined)
export const SessionControlContext = createContext<SessionControl | undefined>(undefined)

export function useSessionControl() {
    const context = useContext(SessionControlContext)
    if (!context) {
        throw new Error("useSessionControl must be used within a SessionProvider")
    }
    return context
}

interface SessionProviderProps {
    children: ReactNode | ReactNode[]
}

export const SessionProvider = (props: SessionProviderProps) => {

    const appFwContext = useAppFwContext()
    const [searchParams, setSearchParams] = useSearchParams()

    const [session, setSession] = useState<undefined | null | Session>()

    const loginListeners = useRef<Set<(session: Session) => Promise<void> | void>>(new Set())
    const logoutListeners = useRef<Set<() => Promise<void> | void>>(new Set())

    const addLoginListener = useCallback((listener: (session: Session) => Promise<void> | void) => {
        loginListeners.current.add(listener)
        return () => { loginListeners.current.delete(listener) }
    }, [])

    const addLogoutListener = useCallback((listener: () => Promise<void> | void) => {
        logoutListeners.current.add(listener)
        return () => { logoutListeners.current.delete(listener) }
    }, [])

    const updateSession = useCallback(async (newSession: null | Session) => {
        // update Session-ID in local storage
        if (newSession) {
            console.log("!!! updating sessionId in local storage", newSession.id)
            localStorage.setItem("sessionId", newSession.id)
            setSession(newSession)
            // notify login listeners
            for (const listener of loginListeners.current) {
                try {
                    await listener(newSession)
                } catch (e) {
                    console.error("Error in login listener", e)
                }
            }
        } else {
            console.log("!!! removing sessionId from local storage")
            localStorage.removeItem("sessionId")
            setSession(null)
        }

    }, [])

    /**
     * Logs out the user and redirects to the given path or origin.
     */
    const logout = useCallback(async (path?: string) => {
        console.log("logout called")

        // notify logout listeners BEFORE clearing session
        for (const listener of logoutListeners.current) {
            try {
                await listener()
            } catch (e) {
                console.error("Error in logout listener", e)
            }
        }

        const storedSessionId = localStorage.getItem("sessionId")
        if (storedSessionId != null) {
            try {
                await appFwContext.appFwApiClient.logout({ sessionId: storedSessionId })
            } catch (err) {
                console.log(`error on logout(${storedSessionId}): ${err} `)
            }
        }

        // Clear session from local storage and state
        updateSession(null)

        if (path) {
            window.location.href = path
        } else {
            window.location.href = window.location.origin
        }
    }, [appFwContext, updateSession])

    const refreshSession = useCallback(async (sessionIdOverride?: string) => {
        const sessionId = sessionIdOverride || localStorage.getItem("sessionId")
        if (sessionId) {
            console.log("refreshing session with sessionId:", sessionId)
            try {
                const authResponse = await appFwContext.appFwApiClient.sessionAuth({ sessionId: sessionId })
                console.log("refreshSession success:", authResponse)
                updateSession({
                    id: authResponse.sessionId,
                    user: authResponse.user,
                    logout: logout,
                    refresh: async () => { await refreshSession() },
                })
            } catch (err) {
                console.log("error on refreshSession:", err)
                updateSession(null)
            }
        } else {
            console.log("no session to refresh")
            updateSession(null)
        }
    }, [appFwContext, logout, updateSession])

    useEffect(() => {
        if (session != undefined) {
            console.log("We already have a valid session, skipping session validation", session)
            return
        }

        const sessionIdFromQuery = searchParams.get("sessionId")
        if (sessionIdFromQuery) {
            console.log("Got 'sessionId' from query: ", sessionIdFromQuery)
        }

        refreshSession(sessionIdFromQuery || undefined).finally(() => {
            if (sessionIdFromQuery) {
                console.log("removing 'sessionId' from query")
                setSearchParams((params) => {
                    const nextParams = new URLSearchParams(params)
                    nextParams.delete("sessionId")
                    return nextParams
                })
            }
        })

    }, [refreshSession, searchParams, session, setSearchParams])

    const sessionControl = useMemo(() => ({
        addLogoutListener,
        addLoginListener,
    }), [addLogoutListener, addLoginListener])

    return (
        <SessionControlContext.Provider value={sessionControl}>
            <SessionContext.Provider value={session}>
                {props.children}
            </SessionContext.Provider>
        </SessionControlContext.Provider>
    )
}
